import { useEffect, useState } from "react";
import { InputAdornment, TextField } from "@mui/material";

// Último dia do mês (1-12) informado — usado pra travar o "dia" digitado nos
// campos de data ao intervalo do mês da competência.
export function ultimoDiaDoMes(ano?: number, mes?: number): number {
  if (!ano || !mes) return 31;
  return new Date(ano, mes, 0).getDate();
}

// Data ISO (aaaa-mm-dd) que cai dentro do mês/ano da competência.
export function dataNaCompetencia(iso: string, ano: number, mes: number): boolean {
  return iso.startsWith(`${ano}-${String(mes).padStart(2, "0")}-`);
}

interface DiaDoMesFieldProps {
  label: string;
  ano?: number;
  mes?: number;
  iso?: string;
  onChangeIso: (iso: string) => void;
  size?: "small" | "medium";
}

// <input type="date"> nativo exibe a data no formato do navegador (Chrome
// ignora até o lang="pt-BR" da página e mostra MM/DD/AAAA) — ruim pro
// usuário mesmo sem afetar o que é salvo. Substituído por um campo de texto
// só pro "dia": mês e ano já vêm da competência escolhida acima (Ano/Mês),
// então em vez de deixar o usuário redigitar os três e validar depois, o
// campo só aceita o dia e mostra o resto ("/mm/aaaa") fixo ao lado — a data
// nunca sai do mês da competência porque não tem como digitar outro.
// Extraído de EscalaPrazosTab (05-plano.md) pra este arquivo compartilhado
// porque EscalaGerarModal precisa da mesma trava pra dia cheio único e
// folga compensatória (01-dominio.md).
export function DiaDoMesField({ label, ano, mes, iso, onChangeIso, size }: DiaDoMesFieldProps) {
  const competenciaDefinida = !!ano && !!mes;
  const maxDia = ultimoDiaDoMes(ano, mes);
  const sufixo = competenciaDefinida ? `/${String(mes).padStart(2, "0")}/${ano}` : "/mm/aaaa";

  const [texto, setTexto] = useState(() => (iso ? iso.split("-")[2] : ""));

  useEffect(() => {
    setTexto(iso ? iso.split("-")[2] : "");
  }, [iso]);

  // Trocar ano ou mês com o dia já preenchido leva a data junto (o dia é mantido, limitado ao
  // último dia do novo mês): sem isso a tela mostra o mês novo e o iso continua no antigo.
  useEffect(() => {
    if (!iso || !competenciaDefinida || dataNaCompetencia(iso, ano, mes)) return;
    const dia = Math.min(Number(iso.split("-")[2]), maxDia);
    onChangeIso(`${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano, mes]);

  const commit = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 2);
    if (!digits || !competenciaDefinida) {
      setTexto(digits);
      onChangeIso("");
      return;
    }
    const dia = Math.min(Math.max(Number(digits), 1), maxDia);
    const diaStr = String(dia).padStart(2, "0");
    setTexto(diaStr);
    onChangeIso(`${ano}-${String(mes).padStart(2, "0")}-${diaStr}`);
  };

  return (
    <TextField
      label={label}
      fullWidth
      required
      size={size}
      disabled={!competenciaDefinida}
      value={texto}
      onChange={(e) => setTexto(e.target.value.replace(/\D/g, "").slice(0, 2))}
      onBlur={(e) => commit(e.target.value)}
      placeholder="dd"
      helperText={competenciaDefinida ? undefined : "Escolha ano e mês primeiro"}
      slotProps={{
        htmlInput: { inputMode: "numeric", maxLength: 2 },
        input: { endAdornment: <InputAdornment position="end">{sufixo}</InputAdornment> },
      }}
    />
  );
}
